import React, { useState, useEffect, useCallback } from 'react'
import { Search, X } from 'lucide-react'
import MapView from './MapView'
import MatrixView from './MatrixView'
import DetailsView from './DetailsView'
import { fetchCimaSuggestions, fetchCimaDetalle } from '../services/cimaService'
import { fetchInteractions } from '../services/interactionService'

export default function InteractionBody({ medications, setMedications }) {
  // ---- Pestañas y zoom ----
  const [tab, setTab] = useState('map')
  const [zoomLevel, setZoom] = useState(1)

  // ---- Buscador CIMA ----
  const [searchTerm, setSearch] = useState('')
  const [suggestions, setSuggestions] = useState([])
  const [loadingSuggestions, setLoadingSuggestions] = useState(false)

  // ---- Sidebar ----
  const [selectedMedication, setSelected] = useState(null)

  // ---- Interacciones y filtros ----
  const [interactions, setInteractions] = useState([])
  const [filters, setFilters] = useState({
    showMild: true,
    showModerate: true,
    showSevere: true,
  })

  // Cuenta por severidad (en inglés)
  const countBySeverity = sev =>
    interactions.filter(i => i.severity === sev).length

  // Debounce de sugerencias
  const fetchSuggestions = useCallback(async term => {
    const q = term.trim().toLowerCase()
    if (!q) {
      setSuggestions([])
      return
    }
    setLoadingSuggestions(true)
    try {
      const items = await fetchCimaSuggestions(q)
      setSuggestions(items.filter(s => s.nombre?.toLowerCase().startsWith(q)))
    } catch {
      setSuggestions([])
    } finally {
      setLoadingSuggestions(false)
    }
  }, [])

  useEffect(() => {
    const q = searchTerm.trim().toLowerCase()
    if (!q) {
      setSuggestions([])
      return
    }
    setLoadingSuggestions(true)
    const h = setTimeout(async () => {
      try {
        const items = await fetchCimaSuggestions(q)
        const filtered = items
          .filter(s => s.nombre?.toLowerCase().includes(q))
          .sort((a, b) => {
            const na = a.nombre.toLowerCase().indexOf(q)
            const nb = b.nombre.toLowerCase().indexOf(q)
            return na - nb
          })
        setSuggestions(filtered)
      } catch {
        setSuggestions([])
      } finally {
        setLoadingSuggestions(false)
      }
    }, 300)
    return () => clearTimeout(h)
  }, [searchTerm, fetchSuggestions])

  // ---- Carga y mapeo de interacciones ----
  useEffect(() => {
    if (medications.length === 0) {
      setInteractions([])
      setSelected(null)
      return
    }

    fetchInteractions()
      .then(all => {
        const activeNames = medications.map(m => m.name)

        // Mapa para pasar de severidad en español a inglés
        const severityMap = {
          leve: 'mild',
          moderada: 'moderate',
          grave: 'severe'
        }

        const filtered = all
          // 1) Filtrar por nombres activos
          .filter(i =>
            activeNames.includes(i.medA_nombre) &&
            activeNames.includes(i.medB_nombre)
          )
          // 2) Mapear al formato unificado
          .map(i => {
            const medA = medications.find(m => m.name === i.medA_nombre)
            const medB = medications.find(m => m.name === i.medB_nombre)
            const sev = severityMap[i.severidad] || 'mild'
            return {
              source: medA.id,
              target: medB.id,
              severity: sev,               // ahora 'mild'|'moderate'|'severe'
              description: i.descripcion,
              recommendation: i.recomendacion,
              fuentes: i.fuentes,
              fromName: i.medA_nombre,
              toName: i.medB_nombre,
            }
          })

        console.log('🔍 Interacciones filtradas:', filtered)
        setInteractions(filtered)
      })
      .catch(err => {
        console.error('❌ Error cargando interacciones:', err)
        setInteractions([])
      })
  }, [medications])

  // ---- Añadir medicamento ----
  const addMedication = async suggestion => {
    if (medications.some(m => m.id === suggestion.nregistro && m.active)) {
      return alert(`${suggestion.nombre} ya está activo.`)
    }
    try {
      const detail = await fetchCimaDetalle(suggestion.nregistro)
      const info = detail[0] || {}
      const newMed = {
        id: info.nregistro || suggestion.nregistro,
        name: info.nombre || suggestion.nombre,
        active: true,
      }
      setMedications(prev => [...prev, newMed])
      setSearch('')
      setSuggestions([])
    } catch (err) {
      console.error('Error obteniendo detalle:', err)
      alert('No se pudo cargar el detalle del medicamento.')
    }
  }

  // ---- Desactivar medicamento ----
  const deactivateMedication = id => {
    setMedications(prev => prev.filter(m => m.id !== id))
    if (selectedMedication?.id === id) setSelected(null)
  }

  // ---- Render ----
  return (
    
    <div className="d-flex flex-fill overflow-hidden">
      {/* Sidebar */}
      <aside className="bg-white p-3 border-end" style={{ width: 260, overflowY: 'auto' }}>
        <h6>Medicamentos activos ({medications.length})</h6>
        {medications.map((m, i) => (
          <div key={m.id || i} className="d-flex align-items-center mb-2">
            <button
              className={`flex-grow-1 text-start py-2 px-3 border rounded ${selectedMedication?.id === m.id
                ? 'bg-info bg-opacity-25 border-info'
                : 'bg-white'
                }`}
              onClick={() => setSelected(m)}
            >
              {m.name}
            </button>
            <button className="btn btn-sm btn-light ms-2" onClick={() => deactivateMedication(m.id)}>
              <X size={16} />
            </button>
          </div>
        ))}

        {/* Buscador */}
        <div className="mt-4">
          <h6>Añadir medicamento</h6>
          <div className="d-flex">
            <input
              className="form-control me-2"
              placeholder="Buscar medicamento..."
              value={searchTerm}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          {loadingSuggestions && <div className="small text-muted">Cargando...</div>}
          {suggestions.length > 0 && (
            <ul className="list-group list-group-flush mt-2">
              {suggestions.map(s => (
                <li
                  key={s.nregistro}
                  className="list-group-item list-group-item-action py-1 px-2"
                  onClick={() => addMedication(s)}
                  style={{ cursor: 'pointer' }}
                >
                  {s.nombre} — {s.pactivos}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Resumen de alertas */}
        <div className="mt-4">
          <h6>Resumen de alertas</h6>
          {['mild', 'moderate', 'severe'].map(sev => {
            // Mapa local de colores “intensos”
            const alertColor = {
              mild: '#FFD966',
              moderate: '#F4984D',
              severe: '#E74C3C'
            }[sev];

            // Etiqueta en español
            const label = { mild: 'Leves', moderate: 'Moderadas', severe: 'Graves' }[sev];

            return (
              <div key={sev} className="d-flex justify-content-between mb-1">
                <span className="d-flex align-items-center gap-2">
                  <span
                    style={{
                      display: 'inline-block',
                      width: 12,
                      height: 12,
                      backgroundColor: alertColor,
                      borderRadius: '50%',
                    }}
                  />
                  {label}
                </span>
                <strong>{countBySeverity(sev)}</strong>
              </div>
            )
          })}
        </div>

        {/* Filtros */}
        <div className="mt-4">
          <h6>Filtros</h6>
          {[
            ['mild', 'Leves', 'showMild'],
            ['moderate', 'Moderadas', 'showModerate'],
            ['severe', 'Graves', 'showSevere'],
          ].map(([sev, label, prop]) => (
            <div key={sev} className="form-check mb-1">
              <input
                id={`f-${sev}`}
                type="checkbox"
                className="form-check-input"
                checked={filters[prop]}
                onChange={() => setFilters(f => ({ ...f, [prop]: !f[prop] }))}
              />
              <label htmlFor={`f-${sev}`} className="form-check-label">
                Mostrar {label.toLowerCase()}
              </label>
            </div>
          ))}
        </div>
      </aside>

      {/* Panel central */}
      <div className="d-flex flex-column flex-fill">
        {/* Tabs */}
        <div className="d-flex border-bottom bg-white">
          {[
            ['map', 'Mapa de interacciones'],
            ['matrix', 'Matriz de interacciones'],
            ['details', 'Detalles y recomendaciones'],
          ].map(([key, label]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              style={{
                flex: 1, padding: '0.5rem 1rem', fontSize: '0.875rem', fontWeight: 500,
                color: tab === key ? '#4723C0' : '#6B7280',
                border: 'none',
                borderBottom: tab === key ? '2px solid #4723C0' : '2px solid transparent',
                background: 'transparent'
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Contenido pestaña */}
        <div className="flex-fill p-3 overflow-auto" style={{ background: '#f0f2f5' }}>
          {tab === 'map' && (
            <MapView
              medications={medications}
              interactions={interactions.filter(i => {
                if (i.severity === 'mild') return filters.showMild
                if (i.severity === 'moderate') return filters.showModerate
                if (i.severity === 'severe') return filters.showSevere
                return true
              })}
              selected={selectedMedication}
              onSelect={setSelected}
              zoomLevel={zoomLevel}
              onZoomIn={() => setZoom(z => Math.min(z + 0.2, 2))}
              onZoomOut={() => setZoom(z => Math.max(z - 0.2, 0.6))}
            />
          )}
          {tab === 'matrix' && (
            <MatrixView
              medications={medications}
              interactions={interactions}
            />
          )}
          {tab === 'details' && (
            <DetailsView
              interactions={interactions}
              medications={medications}
              filters={filters}
            />
          )}
        </div>
      </div>
    </div>
  )
}
